import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getProductBySlug, Product, createInquiryOrder, getAvailableProducts } from '../../services/shopService';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { useTenantContext } from '../../context/TenantContext';
import { ArrowLeft, ShoppingCart, Info, Loader2, Minus, Plus, CheckCircle, Crown, Diamond } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import ProductImageCarousel from '../../components/shop/ProductImageCarousel';

// Mapping d'icônes pour personnalisation - Utilise toutes les icônes Lucide
const iconMap = LucideIcons as Record<string, React.ElementType>;

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };

const ProductDetailPage: React.FC = () => {
  const [tab, setTab] = useState<'about' | 'details'>('about');
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { tenant, previewConfig } = useTenantContext();

  // Thème Boutique (preview > tenant)
  const shopTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'ShoppingBag', color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Sparkles',   color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Diamond',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Star',       color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);
  
  const [product, setProduct] = useState<Product | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [inquiryMessage, setInquiryMessage] = useState('');
  const [similarProducts, setSimilarProducts] = useState<Product[]>([]);

  // --- Produits similaires ---
  useEffect(() => {
    if (product?.category?.slug) {
      getAvailableProducts({ categorySlug: product.category.slug, limit: 4 })
        .then(({ products }) => {
          setSimilarProducts(products.filter(p => p.id !== product.id));
        })
        .catch(() => setSimilarProducts([]));
    }
  }, [product]);

  const fetchProduct = useCallback(async () => {
    if (!slug) return;
    
    setIsLoading(true);
    try {
      const productData = await getProductBySlug(slug);
      setProduct(productData);
      
      // Définir l'image sélectionnée par défaut
      if (productData?.images && productData.images.length > 0) {
        const featuredImage = productData.images.find(img => img.is_featured);
        setSelectedImage(featuredImage?.image_url || productData.images[0].image_url);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du produit:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les détails du produit',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  // Gestionnaire pour ajouter au panier
  const handleAddToCart = () => {
    if (!product) return;
    
    // Récupérer le panier actuel du localStorage
    const cartString = localStorage.getItem('shop_cart');
    const cart = cartString ? JSON.parse(cartString) : [];
    
    // Vérifier si le produit est déjà dans le panier
    const existingItemIndex = cart.findIndex((item: any) => item.productId === product.id);
    
    if (existingItemIndex >= 0) {
      // Mettre à jour la quantité si le produit existe déjà
      cart[existingItemIndex].quantity += quantity;
    } else {
      // Ajouter le nouveau produit au panier
      cart.push({
        productId: product.id,
        title: product.title,
        price: product.price,
        currency: product.currency,
        quantity: quantity,
        imageUrl: selectedImage || product.images?.[0]?.image_url,
        slug: product.slug
      });
    }
    
    // Sauvegarder le panier mis à jour
    localStorage.setItem('shop_cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cart-updated'));
    toast({
      title: 'Produit ajouté au panier',
      description: `${product.title} (${quantity}) a été ajouté à votre panier.`,
      variant: 'default'
    });
  };

  // Gestionnaire pour soumettre une demande d'information
  const handleInquirySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product) return;
    
    setIsSubmitting(true);
    try {
      await createInquiryOrder(product.id, {
        message: inquiryMessage,
        customerEmail: user?.email || '',
        customerName: user?.user_metadata?.full_name || '',
        customerPhone: user?.user_metadata?.phone || ''
      });
      
      toast({
        title: 'Demande envoyée',
        description: 'Nous vous contacterons bientôt avec plus d\'informations sur ce produit',
        variant: 'default'
      });
      
      setShowInquiryForm(false);
      setInquiryMessage('');
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la demande:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'envoyer votre demande. Veuillez réessayer.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Formater le prix
  const formattedPrice = product?.price 
    ? new Intl.NumberFormat('fr-FR').format(product.price) + ' FCFA'
    : 'Prix sur demande';

  // Configuration SEO personnalisée par tenant (doit être avant les returns conditionnels)
  const seoConfig = React.useMemo(() => {
    if (!product) return null;
    
    const seo = tenant?.config?.seo || {};
    const currentDomain = window.location.hostname;
    
    return {
      title: `${product.title} - ${tenant?.name || 'Boutique'}`,
      description: product.description?.substring(0, 160) || `Découvrez ce produit sur ${tenant?.name || 'notre boutique'}.`,
      image: selectedImage || seo.ogImage || tenant?.logo || tenant?.icon,
      url: `https://${currentDomain}/boutique/${product.slug}`
    };
  }, [product, tenant, selectedImage]);

  if (isLoading) {
    return (
      <PublicLayout>
        <div className="min-h-screen relative overflow-hidden">
          {/* Fond avec la nouvelle palette de couleurs SIS */}
          <div 
            className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
            style={{
              backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
              backgroundAttachment: 'fixed'
            }}
          >
            {shopTheme.overlay.enabled && (
              <div className="absolute inset-0 backdrop-blur-[2px]" style={{
                backgroundImage: shopTheme.overlay.useGradient
                  ? `linear-gradient(${(
                      {
                        'to-b': 'to bottom',
                        'to-t': 'to top',
                        'to-r': 'to right',
                        'to-l': 'to left',
                        'to-tr': 'to top right',
                        'to-br': 'to bottom right',
                        'to-tl': 'to top left',
                        'to-bl': 'to bottom left',
                      } as Record<string, string>
                    )[shopTheme.overlay.orientation] || 'to bottom'},
                      ${hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity1)},
                      ${hexToRgba(shopTheme.overlay.color2, shopTheme.overlay.opacity2)},
                      ${hexToRgba(shopTheme.overlay.color3, shopTheme.overlay.opacity3)}
                    )`
                  : undefined,
                backgroundColor: !shopTheme.overlay.useGradient
                  ? hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity2)
                  : undefined,
              }} />
            )}
          </div>

          <div className="relative z-10 flex flex-col justify-center items-center h-screen">
            <Loader2 className="animate-spin h-12 w-12" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
            <span className="mt-6 text-xl text-white/80">Chargement du produit...</span>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (!product) {
    return (
      <PublicLayout>
        <div className="min-h-screen relative overflow-hidden">
          {/* Fond avec la nouvelle palette de couleurs SIS */}
          <div 
            className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
            style={{
              backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
              backgroundAttachment: 'fixed'
            }}
          >
            {shopTheme.overlay.enabled && (
              <div className="absolute inset-0 backdrop-blur-[2px]" style={{
                backgroundImage: shopTheme.overlay.useGradient
                  ? `linear-gradient(${(
                      {
                        'to-b': 'to bottom',
                        'to-t': 'to top',
                        'to-r': 'to right',
                        'to-l': 'to left',
                        'to-tr': 'to top right',
                        'to-br': 'to bottom right',
                        'to-tl': 'to top left',
                        'to-bl': 'to bottom left',
                      } as Record<string, string>
                    )[shopTheme.overlay.orientation] || 'to bottom'},
                      ${hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity1)},
                      ${hexToRgba(shopTheme.overlay.color2, shopTheme.overlay.opacity2)},
                      ${hexToRgba(shopTheme.overlay.color3, shopTheme.overlay.opacity3)}
                    )`
                  : undefined,
                backgroundColor: !shopTheme.overlay.useGradient
                  ? hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity2)
                  : undefined,
              }} />
            )}
          </div>
          
          <div className="relative z-10 container mx-auto py-12">
            <div className="text-center py-20">
              <h2 className="text-3xl font-bold mb-4 text-white">Produit non trouvé</h2>
              <p className="mb-6 text-white/80">Le produit que vous recherchez n'existe pas ou n'est plus disponible.</p>
              <Button asChild className="font-bold"
                style={{ 
                  backgroundColor: tenant?.config?.color1 || '#fe8d17',
                  color: tenant?.config?.color3 || '#ffffff',
                  backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                }}
              >
                <Link to="/boutique">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Retour à la boutique
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      {seoConfig && (
        <SEOMetaTags 
          title={seoConfig.title}
          description={seoConfig.description}
          image={seoConfig.image}
          url={seoConfig.url}
        />
      )}
      {/* OpenGraph & Twitter Card balises SEO */}
      {seoConfig && (
        <>
          <meta property="og:type" content="product" />
          <meta property="og:title" content={seoConfig.title} />
          <meta property="og:description" content={seoConfig.description} />
          <meta property="og:image" content={seoConfig.image} />
          <meta property="og:url" content={seoConfig.url} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={seoConfig.title} />
          <meta name="twitter:description" content={seoConfig.description} />
          <meta name="twitter:image" content={seoConfig.image} />
        </>
      )}
      {/* schema.org Product microdata */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org/',
        '@type': 'Product',
        name: product.title,
        image: product.images?.map(img => img.image_url),
        description: product.description,
        sku: product.id,
        offers: {
          '@type': 'Offer',
          url: `https://ecatheba.com/boutique/${product.slug}`,
          priceCurrency: 'XAF',
          price: product.price,
          availability: product.stock_quantity > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
        }
      }) }} />

      <div className="text-white relative overflow-hidden min-h-screen">
        {/* Fond avec la nouvelle palette de couleurs SIS */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
            backgroundAttachment: 'fixed'
          }}
        >
          {shopTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: shopTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[shopTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity1)},
                    ${hexToRgba(shopTheme.overlay.color2, shopTheme.overlay.opacity2)},
                    ${hexToRgba(shopTheme.overlay.color3, shopTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !shopTheme.overlay.useGradient
                ? hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>
        
        {/* Particules flottantes avec couleurs et icônes personnalisables */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top-Left */}
          {shopTheme.icons[0]?.enabled && (
            <motion.div
              className="absolute top-20 left-10"
              style={{ color: hexToRgba(shopTheme.icons[0].color, 0.2) }}
              animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[0].icon] || iconMap.ShoppingCart;
                return IconComponent ? <IconComponent size={shopTheme.icons[0].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Top-Right */}
          {shopTheme.icons[1]?.enabled && (
            <motion.div
              className="absolute top-40 right-20"
              style={{ color: hexToRgba(shopTheme.icons[1].color, 0.2) }}
              animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[1].icon] || iconMap.Sparkles;
                return IconComponent ? <IconComponent size={shopTheme.icons[1].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Left */}
          {shopTheme.icons[2]?.enabled && (
            <motion.div
              className="absolute bottom-40 left-20"
              style={{ color: hexToRgba(shopTheme.icons[2].color, 0.2) }}
              animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[2].icon] || iconMap.Diamond;
                return IconComponent ? <IconComponent size={shopTheme.icons[2].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Right */}
          {shopTheme.icons[3]?.enabled && (
            <motion.div
              className="absolute bottom-20 right-10"
              style={{ color: hexToRgba(shopTheme.icons[3].color, 0.2) }}
              animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
              transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[3].icon] || iconMap.Star;
                return IconComponent ? <IconComponent size={shopTheme.icons[3].size} /> : null;
              })()}
            </motion.div>
          )}
        </div>

        <div className="relative z-20 container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 md:pt-24 md:pb-20">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="flex items-center gap-4 mb-8">
            <Button 
              variant="ghost"
              onClick={() => navigate('/boutique')}
              className="backdrop-blur-sm"
              style={{ 
                color: tenant?.config?.color4 || '#9ca3af',
                borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = tenant?.config?.color3 || '#ffffff';
                e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}33`;
                e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = tenant?.config?.color4 || '#9ca3af';
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}4d`;
              }}
            >
              <ArrowLeft size={16} className="mr-2" />
              Retour à la boutique
            </Button>
            <h1 className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">{product.title}</h1>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12">
            {/* Colonne des images */}
            <motion.div className="lg:col-span-3" variants={fadeIn} initial="hidden" animate="visible" transition={{ duration: 0.7, ease: 'easeOut' }}>
              <div>
                <ProductImageCarousel 
                  images={product.images?.map(img => ({
                    ...img,
                    alt_text: img.alt_text || undefined
                  })) || []} 
                  initialImage={selectedImage || undefined} 
                />
              </div>
            </motion.div>

            {/* Colonne des informations */}
            <motion.div className="lg:col-span-2 bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 shadow-2xl text-white" variants={fadeIn} initial="hidden" animate="visible">
              {product.category && (
                 <Link to={`/boutique?categorie=${product.category.slug}`}>
                  <Badge variant="secondary" className="backdrop-blur-sm transition-colors"
                    style={{ 
                      backgroundColor: `${tenant?.config?.color3 || '#fe8d17'}33`,
                      color: tenant?.config?.color3 || '#ffffff',
                      borderColor: `${tenant?.config?.color1 || '#fe8d17'}80`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}4d`;
                      e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}33`;
                      e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}80`;
                    }}
                  >
                    {product.category.name}
                  </Badge>
                </Link>
               )}
               {product.is_unique && (
                  <Badge variant="default" className="backdrop-blur-sm"
                    style={{ 
                      backgroundColor: tenant?.config?.color1 || '#fe8d17',
                      color: tenant?.config?.color3 || '#ffffff',
                      borderColor: `${tenant?.config?.color1 || '#fe8d17'}cc`,
                    }}
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Produit Unique
                  </Badge>
                )}
              {product.stock_quantity && product.stock_quantity <= 5 && (
                  <Badge variant="outline" className="backdrop-blur-sm"
                    style={{ 
                      color: tenant?.config?.color1 || '#fe8d17',
                      borderColor: `${tenant?.config?.color1 || '#fe8d17'}80`,
                    }}
                  >
                    <Diamond className="w-4 h-4 mr-2" />
                    Stock Limité
                  </Badge>
                )}

              {/* Bloc Détails de la ressource */}
              <motion.div className="space-y-2 mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: 'easeOut' }}>
                {product.price && (
                  <motion.div className="text-4xl font-bold mb-2" aria-label="Prix" initial={{ scale: 0.8 }} animate={{ scale: 1 }} transition={{ duration: 0.5 }}>
                    <span aria-hidden="true" style={{ color: tenant?.config?.color1 || '#fe8d17' }}>💰</span> 
                    <span style={{ 
                      backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e07a10'})`,
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      {formattedPrice}
                    </span>
                  </motion.div>
                )}
                {product.stock_quantity !== undefined && (
                  <motion.div className="text-3xl text-white/80 text-sm" aria-label="Stock" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
                    <span aria-hidden="true">📦</span> Stock : {product.stock_quantity}
                  </motion.div>
                )}
                {product.dimensions && (
                  <motion.div className="text-3xl text-white/80 text-sm" aria-label="Dimensions" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
                    <span aria-hidden="true">📏</span> Dimensions : <span className="font-semibold text-white">{product.dimensions}</span>
                  </motion.div>
                )}
                {product.materials && product.materials.length > 0 && (
                  <motion.div className="text-3xl text-white/80 text-sm flex flex-wrap items-center gap-2" aria-label="Matériaux" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
                    <span aria-hidden="true">📚</span> Matériaux :
                    {product.materials.map((mat, i) => (
                      <motion.span key={i} className="rounded-full px-3 py-1 text-xs ml-2 backdrop-blur-sm" initial={{ scale: 0.7 }} animate={{ scale: 1 }} transition={{ delay: 0.35 + i * 0.05 }}
                        style={{
                          backgroundColor: `${tenant?.config?.color1 || '#fe8d17'}33`,
                          borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                          color: tenant?.config?.color3 || '#ffffff'
                        }}
                      >
                        {mat}
                      </motion.span>
                    ))}
                  </motion.div>
                )}
                {product.year && (
                  <motion.div className="text-white/80 text-sm" aria-label="Année" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
                    <span aria-hidden="true">📅</span> Année : <span className="font-semibold text-white">{product.year}</span>
                  </motion.div>
                )}
                {product.weight_kg && (
                  <motion.div className="text-white/80 text-sm" aria-label="Poids" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
                    <span aria-hidden="true">⚖️</span> Poids : <span className="font-semibold text-white">{product.weight_kg} kg</span>
                  </motion.div>
                )}
              </motion.div>

              <h1 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight drop-shadow-sm">{product.title}</h1>

              {/* Actions d'achat */}
              <div className="rounded-lg p-6 mb-6 backdrop-blur-sm"
                style={{
                  backgroundColor: `${tenant?.config?.color3 || '#fe8d17'}1a`,
                  borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`
                }}
              >
                {product.stock_quantity > 0 ? (
                  <>
                    {/* Sélecteur de quantité */}
                    <div className="mb-6">
                      <label htmlFor="quantity" className="block text-sm font-medium text-white mb-2">Quantité</label>
                      <div className="flex items-center space-x-3">
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          className="backdrop-blur-sm transition-all duration-300"
                          style={{
                            borderColor: tenant?.config?.color3 || '#fe8d17',
                            color: tenant?.config?.color3 || '#ffffff'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}33`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          aria-label="Diminuer la quantité"
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                        <span className="text-lg font-semibold text-white min-w-[3rem] text-center">{quantity}</span>
                        <Button 
                          variant="outline" 
                          size="icon"
                          onClick={() => setQuantity(quantity + 1)}
                          className="backdrop-blur-sm transition-all duration-300"
                          style={{
                            borderColor: tenant?.config?.color3 || '#fe8d17',
                            color: tenant?.config?.color3 || '#ffffff'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}33`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                          aria-label="Augmenter la quantité"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4, ease: 'easeOut' }}>
                      <Button
                        onClick={handleAddToCart}
                        size="lg"
                        className="w-full shadow-lg transform transition-transform duration-150 active:scale-95 font-bold"
                        style={{
                          backgroundColor: tenant?.config?.color1 || '#fe8d17',
                          color: tenant?.config?.color3 || '#ffffff',
                          backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e07a10'})`,
                          boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`,
                          '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e07a10'})`;
                        }}
                        aria-label="Ajouter ce produit au panier"
                      >
                        <ShoppingCart size={20} className="mr-2" aria-hidden="true" />
                        Ajouter au panier
                      </Button>
                    </motion.div>
                  </>
                ) : (
                  <Button onClick={() => setShowInquiryForm(true)} size="lg" className="w-full font-bold backdrop-blur-sm"
                    style={{
                      backgroundColor: tenant?.config?.color2 || '#08062b',
                      color: tenant?.config?.color3 || '#ffffff',
                      borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${tenant?.config?.color2 || '#08062b'}e6`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = tenant?.config?.color2 || '#08062b';
                    }}
                  >
                    <Info size={20} className="mr-2" />
                    Demander des informations
                  </Button>
                )}
              </div>

              {showInquiryForm && ! (product.stock_quantity > 0) && (
                <Card className="mt-6 backdrop-blur-sm text-white"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`
                  }}
                >
                  <CardHeader>
                    <CardTitle style={{ color: tenant?.config?.color3 || '#ffffff' }}>Demande d'informations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleInquirySubmit}>
                      <Textarea
                        value={inquiryMessage}
                        onChange={(e) => setInquiryMessage(e.target.value)}
                        placeholder="Précisez votre demande concernant ce produit..."
                        className="mb-4 placeholder:text-white/60 backdrop-blur-sm"
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                          color: tenant?.config?.color3 || '#ffffff',
                          '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                        } as React.CSSProperties}
                        rows={5}
                        required
                      />
                      {!user && (
                        <p className="text-sm mb-4" style={{ color: tenant?.config?.color4 || '#9ca3af' }}> 
                          <Link to="/login?redirect=/boutique/" className="hover:underline transition-colors" style={{ color: tenant?.config?.color1 || '#fe8d17' }}>Connectez-vous</Link> pour un suivi plus rapide.
                        </p>
                      )}
                      <div className="flex justify-end gap-3">
                        <Button type="button" variant="ghost" onClick={() => setShowInquiryForm(false)} className="backdrop-blur-sm"
                          style={{
                            color: tenant?.config?.color4 || '#9ca3af'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = tenant?.config?.color3 || '#ffffff';
                            e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}1a`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = tenant?.config?.color4 || '#9ca3af';
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >Annuler</Button>
                        <Button type="submit" className="font-semibold backdrop-blur-sm"
                          style={{
                            backgroundColor: tenant?.config?.color1 || '#fe8d17',
                            color: tenant?.config?.color3 || '#ffffff',
                            backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e07a10'})`
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e07a10'})`;
                          }}
                        >
                          {isSubmitting ? <Loader2 className="animate-spin mr-2"/> : null}
                          Envoyer
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          </div>

          {/* Description détaillée */}
          {product.description && (
            <motion.div className="mt-12 lg:mt-20" variants={fadeIn} initial="hidden" whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="w-full">
                <div className="bg-white/5 backdrop-blur-sm rounded-xl border p-6 md:p-10 shadow-lg"
                  style={{
                    borderColor: `${tenant?.config?.color3 || '#fe8d17'}4d`
                  }}
                >
                  {/* Onglets */}
                  <div className="flex border-b mb-6"
                    style={{
                      borderColor: `${tenant?.config?.color3 || '#fe8d17'}33`
                    }}
                  >
                    <button
                      className="px-6 py-2 text-lg font-bold transition border-b-2"
                      style={{
                        borderColor: tab === 'about' ? (tenant?.config?.color3 || '#fe8d17') : 'transparent',
                        color: tab === 'about' ? tenant?.config?.color3 || '#ffffff' : `${tenant?.config?.color4 || '#9ca3af'}cc`
                      }}
                      onClick={() => setTab('about')}
                      type="button"
                    >
                      À propos du produit
                    </button>
                    {product.content && (
                      <button
                        className="px-6 py-2 text-lg font-bold transition border-b-2"
                        style={{
                          borderColor: tab === 'details' ? (tenant?.config?.color3 || '#fe8d17') : 'transparent',
                          color: tab === 'details' ? tenant?.config?.color3 || '#ffffff' : `${tenant?.config?.color4 || '#9ca3af'}cc`
                        }}
                        onClick={() => setTab('details')}
                        type="button"
                      >
                        Détails complémentaires
                      </button>
                    )}
                  </div>
                  {/* Contenu onglets */}
                  {tab === 'about' && (
                    <div
                      className="prose prose-lg max-w-none prose-headings:text-white prose-p:text-white/80 prose-strong prose-a:no-underline hover:prose-a:underline prose-blockquote:bg-white/10 prose-blockquote:p-4 prose-blockquote:rounded-md prose-blockquote:not-italic prose-blockquote:text-white/80 prose-img:rounded-lg"
                      style={{
                        '--tw-prose-strong-color': tenant?.config?.color1 || '#fe8d17',
                        '--tw-prose-a-color': tenant?.config?.color1 || '#fe8d17',
                        '--tw-prose-blockquote-border-color': tenant?.config?.color1 || '#fe8d17'
                      } as React.CSSProperties}
                      dangerouslySetInnerHTML={{ __html: product.description }}
                    />
                  )}
                  {tab === 'details' && product.content && (
                    <div
                      className="prose prose-lg max-w-none prose-headings:text-white prose-p:text-white/80 prose-strong prose-a:no-underline hover:prose-a:underline prose-blockquote:bg-white/10 prose-blockquote:p-4 prose-blockquote:rounded-md prose-blockquote:not-italic prose-blockquote:text-white/80 prose-img:rounded-lg"
                      style={{
                        '--tw-prose-strong-color': tenant?.config?.color1 || '#fe8d17',
                        '--tw-prose-a-color': tenant?.config?.color1 || '#fe8d17',
                        '--tw-prose-blockquote-border-color': tenant?.config?.color1 || '#fe8d17'
                      } as React.CSSProperties}
                      dangerouslySetInnerHTML={{ __html: product.content }}
                    />
                  )}
                </div>
              </div>
            </motion.div>
          )}
          {/* Section Produits similaires */}
          {similarProducts && similarProducts.length > 0 && (
            <motion.div className="mt-20" variants={fadeIn} initial="hidden" whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="max-w-6xl mx-auto">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-8 drop-shadow-lg">Produits similaires</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                  {similarProducts.map((prod: any) => (
                    <div key={prod.id} className="group block rounded-xl overflow-hidden bg-white/5 backdrop-blur-sm border shadow-lg hover:shadow-2xl transition-all duration-300 relative"
                      style={{
                        borderColor: `${tenant?.config?.color3 || '#fe8d17'}4d`
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}4d`;
                      }}
                    >
                      <Link to={`/boutique/produit/${prod.slug}`} className="absolute inset-0 z-10" tabIndex={-1} aria-label={prod.title}></Link>
                      <div className="aspect-square overflow-hidden">
                        <div className="relative w-full h-full">
                          <img
                            src={prod.images?.[0]?.image_url?.replace(/\.jpg|\.png/, '.webp') || prod.images?.[0]?.image_url || 'https://via.placeholder.com/400x400'}
                            alt={prod.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            loading="lazy"
                            onError={e => { e.currentTarget.src = prod.images?.[0]?.image_url || 'https://via.placeholder.com/400x400'; }}
                            aria-label={`Image de ${prod.title}`}
                          />
                        </div>
                      </div>
                      <div className="p-4 relative z-20">
                        <div className="text-lg font-semibold text-white mb-1 truncate">{prod.title}</div>
                        {prod.price && (
                          <div className="font-bold bg-clip-text text-transparent text-lg drop-shadow"
                            style={{
                              backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e07a10'})`,
                              WebkitBackgroundClip: 'text',
                              backgroundClip: 'text',
                              WebkitTextFillColor: 'transparent'
                            }}
                          >
                            {new Intl.NumberFormat('fr-FR').format(prod.price)} FCFA
                          </div>
                        )}
                        <Button
                          size="icon"
                          aria-label="Ajouter ce produit au panier"
                          className="absolute top-4 right-4 shadow-lg transition-all duration-300 group w-10 h-10"
                          style={{
                            backgroundColor: tenant?.config?.color2 || '#08062b',
                            borderColor: `${tenant?.config?.color1 || '#fe8d17'}80`
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = tenant?.config?.color1 || '#fe8d17';
                            e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = tenant?.config?.color2 || '#08062b';
                            e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}80`;
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            // Ajout au panier similaire à handleAddToCart
                            const cartString = localStorage.getItem('shop_cart');
                            const cart = cartString ? JSON.parse(cartString) : [];
                            const existingItemIndex = cart.findIndex((item: any) => item.productId === prod.id);
                            if (existingItemIndex >= 0) {
                              cart[existingItemIndex].quantity += 1;
                            } else {
                              cart.push({
                                productId: prod.id,
                                title: prod.title,
                                price: prod.price,
                                currency: 'XAF',
                                quantity: 1,
                                imageUrl: prod.images?.[0]?.image_url,
                                slug: prod.slug
                              });
                            }
                            localStorage.setItem('shop_cart', JSON.stringify(cart));
                            window.dispatchEvent(new Event('cart-updated'));
                            toast({
                              title: 'Produit ajouté au panier',
                              description: `${prod.title} (1) a été ajouté à votre panier.`,
                              variant: 'default'
                            });
                          }}
                        >
                          <ShoppingCart size={16} className="group-hover:scale-110 transition-transform" />
                          <span className="absolute -top-1 -right-1 rounded-full text-xs w-5 h-5 flex items-center justify-center font-bold group-hover:scale-110 transition-transform"
                            style={{
                              backgroundColor: tenant?.config?.color1 || '#fe8d17',
                              color: tenant?.config?.color3 || '#ffffff'
                            }}
                          >+</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </PublicLayout>
  );
};

const cartUpdateEvent = new Event('cart-updated');

function MiniCartPreview() {
  const { tenant } = useTenantContext();
  const [cart, setCart] = React.useState<any[]>([]);
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    const updateCart = () => {
      const cartString = localStorage.getItem('shop_cart');
      setCart(cartString ? JSON.parse(cartString) : []);
    };
    updateCart();
    window.addEventListener('storage', updateCart);
    window.addEventListener('cart-updated', updateCart);
    return () => {
      window.removeEventListener('storage', updateCart);
      window.removeEventListener('cart-updated', updateCart);
    };
  }, []);
  return (
    <div className="fixed top-32 md:top-24 right-4 md:right-10 z-50">
      <button
        aria-label="Aperçu du panier"
        className="relative backdrop-blur-sm rounded-full p-3 shadow-lg hover:scale-110 transition-all duration-300"
        style={{
          backgroundColor: `${tenant?.config?.color2 || '#08062b'}e6`,
          borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = tenant?.config?.color1 || '#fe8d17';
          e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = `${tenant?.config?.color2 || '#08062b'}e6`;
          e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}4d`;
        }}
        onClick={() => setOpen(v => !v)}
      >
        <svg className="w-6 h-6 text-white transition-colors" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2 9m13-9l2 9m-5-9V6a2 2 0 10-4 0v3m4 0h-4" /></svg>
        {cart.length > 0 && <span className="absolute -top-1 -right-1 rounded-full text-xs px-2 py-0.5 font-bold animate-pulse"
          style={{
            backgroundColor: tenant?.config?.color1 || '#fe8d17',
            color: tenant?.config?.color3 || '#ffffff'
          }}
        >{cart.length}</span>}
      </button>
      {open && (
        <div className="mt-2 w-80 backdrop-blur-sm rounded-xl shadow-2xl p-4 relative"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
            color: tenant?.config?.color3 || '#ffffff'
          }}
        >
          <button
            aria-label="Fermer le mini-panier"
            className="absolute top-2 right-2 text-xl font-bold transition-colors"
            style={{
              color: `${tenant?.config?.color4 || '#9ca3af'}b3`
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = `${tenant?.config?.color4 || '#9ca3af'}b3`;
            }}
            onClick={() => setOpen(false)}
            tabIndex={0}
          >×</button>
          <div className="flex justify-between items-center mb-2 pr-6">
            <span className="font-bold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Mon panier</span>
            <Link to="/boutique/panier" className="hover:underline transition-colors"
              style={{
                color: tenant?.config?.color1 || '#fe8d17'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = `${tenant?.config?.color1 || '#fe8d17'}cc`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
              }}
            >Voir tout</Link>
          </div>
          {cart.length === 0 ? (
            <div className="text-sm" style={{ color: `${tenant?.config?.color4 || '#9ca3af'}99` }}>Votre panier est vide.</div>
          ) : (
            <ul style={{ borderColor: `${tenant?.config?.color1 || '#fe8d17'}33` }} className="divide-y">
              {cart.map(item => (
                <li key={item.productId} className="py-2 grid grid-cols-6 items-center gap-2">
                  {/* Image 1/6 */}
                  <div className="col-span-1 flex items-center">
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="w-10 h-10 rounded object-cover"
                        style={{
                          backgroundColor: `${tenant?.config?.color1 || '#fe8d17'}1a`,
                          borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`
                        }}
                        onError={e => { e.currentTarget.src = item.imageUrl || 'https://via.placeholder.com/400x400'; }}
                        aria-label={"Image de " + item.title}
                      />
                    ) : (
                      <div className="w-10 h-10 flex items-center justify-center rounded"
                        style={{
                          backgroundColor: `${tenant?.config?.color1 || '#fe8d17'}1a`,
                          borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`
                        }}
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ color: `${tenant?.config?.color4 || '#9ca3af'}99` }}><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2 9m13-9l2 9m-5-9V6a2 2 0 10-4 0v3m4 0h-4" /></svg>
                      </div>
                    )}
                  </div>
                  {/* Titre + quantité 3/6 */}
                  <div className="col-span-3 min-w-0">
                    <div className="font-semibold truncate" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{item.title}</div>
                    <div className="text-xs" style={{ color: `${tenant?.config?.color4 || '#9ca3af'}b3` }}>x{item.quantity}</div>
                  </div>
                  {/* Prix 1/6 */}
                  <div className="col-span-1 text-sm font-bold text-right" style={{ color: tenant?.config?.color1 || '#fe8d17' }}>
                    {new Intl.NumberFormat('fr-FR').format(item.price * item.quantity)} FCFA
                  </div>
                  {/* Icône suppression 1/6 */}
                  <div className="col-span-1 flex justify-end">
                    <Button
                      aria-label="Retirer du panier"
                      size="icon"
                      variant="ghost"
                      className="transition-colors"
                      style={{
                        color: `${tenant?.config?.color4 || '#9ca3af'}66`
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
                        e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}1a`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = `${tenant?.config?.color4 || '#9ca3af'}66`;
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      onClick={() => {
                        const updated = cart.filter(i => i.productId !== item.productId);
                        setCart(updated);
                        localStorage.setItem('shop_cart', JSON.stringify(updated));
                      }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3m5 0H6" /></svg>
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProductDetailPageWithCartPreview(props: any) {
  return <>
    <MiniCartPreview />
    <ProductDetailPage {...props} />
  </>;
}

// export default ProductDetailPage;
